import { Input, Spin, Typography } from 'antd';
import React, { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import WizardForm from '~/components/WizardForm';

import { useGetPayloadWithProvided } from '~/controllers/wizard';
import Section from '~/layouts/Section ';
import { Stage } from '~/types/Application';
import { Draft } from '~/types/Draft';
import buildNamePath from '~/utils/buildNamePath';
import { KsaBankAccount } from '~/types/ksa/KsaBankAccount';
import { DocumentInput, Form } from '~/components';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import { useHandbook, useHandbookKeys } from '~/controllers/handbook';
import ReviewItem from '../components/ReviewItem';
import StepReviewComment from '../components/StepReviewComment';
import { detectFieldIssue } from '~/utils/detectFieldIssue';
import AgentMissingValueLabel from '~/components/AgentMissingValueLabel';
import {
  ENGLISH_BANK_NAME,
  SAUDI_IBAN,
  SWIFT_CODE,
  getDerivedBankUpdates,
  getEditedBankFields,
  getInitialBankReview,
  isBankVerified,
  normalizeIban,
  normalizeBankReview,
  normalizeSwiftCode,
  validateSaudiIban,
} from './helpers';

const n = buildNamePath('reviewBankAccount');

const BankAccount: FC = () => {
  const { t } = useTranslation('ksaReview');

  const [form] = WizardForm.useForm();
  const currentIban = WizardForm.useWatch(n(['agentIban']), form);
  const editedFields = useRef(new Set<string>());

  const payload = useGetPayloadWithProvided();

  const handbook = useHandbook();
  const handbookKeys = useHandbookKeys();

  const banks = handbook.data?.handbooks?.find((item) => item.key === handbookKeys.bank);

  const bankAccount = payload?.bankAccount as KsaBankAccount | undefined;
  const verified = isBankVerified(bankAccount, currentIban);
  const beneficiary = bankAccount?.accountHolderName?.trim() || bankAccount?.beneficiaryName;

  if (!payload) return null;
  if (handbook.isLoading && !handbook.data) return <Spin />;

  return (
    <Container className="ReviewBankAccount">
      <WizardForm<Draft<'reviewBankAccount'>>
        stage={Stage.ksaReview}
        payloadKey="reviewBankAccount"
        form={form}
        parseBeforeSubmit={(values) => ({
          ...values,
          reviewBankAccount: normalizeBankReview(values.reviewBankAccount),
        })}
        setInitialValues={(values) => {
          const saved = payload?.reviewBankAccount;
          const draft = values.reviewBankAccount;
          editedFields.current = getEditedBankFields(saved, draft, banks?.values);
          return {
            ...values,
            reviewBankAccount: getInitialBankReview(bankAccount, saved, draft, banks?.values),
          };
        }}
        onValuesChange={(changed, values) => {
          const reviewChanges = changed.reviewBankAccount || {};
          Object.keys(reviewChanges).forEach((key) => editedFields.current.add(key));
          if (Object.prototype.hasOwnProperty.call(reviewChanges, 'agentIban')) {
            // A SWIFT override belongs to the previous IBAN; allow a new correction after derivation.
            editedFields.current.delete('agentSwiftCode');
            const updates = getDerivedBankUpdates(
              reviewChanges.agentIban || '',
              banks?.values,
              editedFields.current
            );
            form.setFieldsValue({ reviewBankAccount: updates });
            // WizardForm autosaves this snapshot; setFieldsValue alone does not trigger autosave.
            Object.assign(values.reviewBankAccount, updates);
          }
        }}
      >
        <Typography.Title style={{ marginBottom: 40 }}>{t('Bank account')}</Typography.Title>
        <div style={{ marginBottom: 40 }}>
          <ReviewItem label={t('Verification Status')}>
            <span>{verified ? t('Yes') : t('No')}</span>
          </ReviewItem>
        </div>
        <Section formItemLast>
          <ReviewItem label={t('IBAN letter')}>
            {bankAccount?.files ? (
              <div onClick={(e) => e.preventDefault()} aria-hidden>
                <DocumentInput
                  fullWidth
                  name=""
                  maxCount={bankAccount.files.length}
                  value={bankAccount?.files}
                  noRemove
                  multiple={false}
                  disabled
                />
              </div>
            ) : undefined}
          </ReviewItem>

          <InputsGroupLayout>
            <ReviewItem label={t('Beneficiary name')}>
              {beneficiary}
              <AgentMissingValueLabel issue={detectFieldIssue(beneficiary)} />
            </ReviewItem>
            <Form.Item
              label={t('Beneficiary name – Agent Updates')}
              name={n(['englishBeneficiaryFullName'])}
              validateFirst
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: t('Please enter the English beneficiary name'),
                },
                {
                  pattern: ENGLISH_BANK_NAME,
                  transform: (value) => value?.trim(),
                  message: t('English letters and spaces only'),
                },
              ]}
            >
              <Input />
            </Form.Item>
            <ReviewItem label={t('Bank name')}>
              {bankAccount?.bankName}
              <AgentMissingValueLabel issue={detectFieldIssue(bankAccount?.bankName)} />
            </ReviewItem>
            <Form.Item
              label={t('Bank name – Agent Updates')}
              name={n(['agentBankName'])}
              validateFirst
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: t('Please enter the English bank name'),
                },
                {
                  pattern: ENGLISH_BANK_NAME,
                  transform: (value) => value?.trim(),
                  message: t('English letters and spaces only'),
                },
              ]}
            >
              <Input />
            </Form.Item>
            {!isBankVerified(bankAccount) && bankAccount?.bankIntegrationRes?.internalMessage && (
              <ReviewItem label={t('Error Code')}>
                {bankAccount?.bankIntegrationRes?.internalMessage}
              </ReviewItem>
            )}
            <Form.Item
              label={t('IBAN')}
              name={n(['agentIban'])}
              validateFirst
              normalize={normalizeIban}
              rules={[
                { required: true, message: t('Please enter the IBAN') },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const errorKey = validateSaudiIban(value);
                    if (!errorKey) return Promise.resolve();
                    return Promise.reject(new Error(t(errorKey)));
                  },
                },
              ]}
            >
              <Input dir="ltr" />
            </Form.Item>
            <Form.Item label={t('Derived Bank Name')} name={n(['derivedBankName'])}>
              <Input />
            </Form.Item>
            <Form.Item
              label={t('Derived Swift Code')}
              name={n(['agentSwiftCode'])}
              validateFirst
              normalize={normalizeSwiftCode}
              rules={[
                {
                  required: !verified,
                  whitespace: true,
                  message: t('Please enter the Swift Code'),
                },
                {
                  pattern: SWIFT_CODE,
                  transform: (value) => value && normalizeSwiftCode(value),
                  message: t('Enter a valid 8 or 11 character Swift Code'),
                },
              ]}
            >
              <Input dir="ltr" />
            </Form.Item>
          </InputsGroupLayout>
        </Section>
        <Section>
          <StepReviewComment n={n} />
        </Section>
      </WizardForm>
    </Container>
  );
};

const Container = styled.div``;

export default BankAccount;
